import React, { FC, ReactElement } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { clsx } from 'clsx';
// Components
import { Moment } from 'moment';
import PageContainer from '../../../components/layout/pageContainer';
import NoteCard from '../../../components/bookingExperience/noteCards';
import ReservationCard from '../../../components/reservations/reservationCard';
import TextButton from '../../../components/buttons/textButton';
import EditButtonList, { EditButtonInfos } from '../../../components/profile/editButtonList';
import Separator from '../../../components/layout/separator';
import Space from '../../../components/layout/space';
import InfoBox from '../../../components/infoBox';
import Text from '../../../components/text';
import LoaderFullPage from '../../../components/loaderFullPage';
import EditReservationDriverMessage from './editReservationDriverMessage';
import { ModalSize } from '../../../components/modals/createModalHOC';
import EditReservationRideReason from './editReservationRideReason';
// Modules
import { actions as currentBookingActions } from '../../../core/modules/currentBooking';
import { actions as modalActions, ActionOpenModal, ModalType } from '../../../core/modules/modals';
// Hooks
import useTimeoutFn from '../../../components/hook/useTimeoutFn';
import {
    useCancelSOSRide,
    useGetRideRecurring,
    useGetSpecificRide,
    usePauseRideRecurrent,
    useSOSRide,
    useUnPauseRideRecurrent,
} from '../../../core/services/rides';
import useGlobalStyles from '../../../components/hook/useGlobalStyles';
import { useReservationCancellation } from '../../../hooks/useReservationCancellation';
// Resources
import { paths } from '../paths';
import { stringsRes } from '../../../resources/strings';
import { Break, PaymentCard, Ride, StopType } from '../../../core/apiTypes';
import {
    getInProgressStatus,
    getCancellableStatus,
    getFinishedAndCancelledStatus,
    findStop,
    isRidePro,
    isBookingFinishedNotCanceled,
    getSOSStatus,
    isAirport,
    isClearanceBlocked,
    isRideCancelledForPayment,
} from '../../../utils/business';
import { REFRESH_TIME_SOON } from '../../../utils/constants';
import { useGetPaymentCards } from '../../../core/services/payments';
import { useBreakpoints, WebLocation } from '../../../customHooks/useBreakpoints';
import { ButtonType } from '../../../components/modals/templates/modalButton';
import { isWebView } from '../../../utils/webview';
import { client } from '../../../core/services/helpers';

interface ReservationDetailsProps {
    preloadReservation?: Ride;
    recurrent?: boolean;
    prefillRide: (ride: Ride, paymentList: PaymentCard[]) => any;
    returnTrip: () => any;
    setIsEditing: (isEditing: boolean) => any;
    setShouldAvoidPoi: (value: boolean) => any;
    setCanSkipPoi: (value: boolean) => any;
    openModal: ActionOpenModal;
}

const ReservationDetails: FC<ReservationDetailsProps> = (props): ReactElement => {
    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const navigate = useNavigate();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);
    const isRideTrackingPage = window.location.pathname.includes(paths.homeTrackingMobile.replace(':id', ''));

    const {
        preloadReservation,
        recurrent,
        prefillRide,
        returnTrip,
        setIsEditing,
        openModal,
        setShouldAvoidPoi,
        setCanSkipPoi,
    } = props;

    const { id } = useParams();
    const queryRes = (recurrent ? useGetRideRecurring : useGetSpecificRide)<Ride>({
        enabled: !!id && !preloadReservation,
        variables: { id },
        onSuccess: () => {
            reset();
        },
    });
    let { data: reservation } = queryRes;
    const { refetch } = queryRes;

    if (preloadReservation) {
        reservation = preloadReservation;
    }

    const [, , reset] = useTimeoutFn(refetch, REFRESH_TIME_SOON);

    const sosActivate = useSOSRide();
    const sosCancel = useCancelSOSRide();
    const pauseRecurrence = usePauseRideRecurrent();
    const unPauseRecurrence = useUnPauseRideRecurrent();

    const { handleCancelReservation } = useReservationCancellation({
        reservation,
        recurrent,
        openModal,
        onSuccess: () => {
            navigate(paths.reservations);
        },
    });

    const { data: paymentList } = useGetPaymentCards<PaymentCard[]>({
        enabled: !!reservation,
        variables: { group_id: reservation?.group?.uuid },
    });

    // Move the user to the history page if canceled
    if (reservation?.status && getFinishedAndCancelledStatus().includes(reservation.status) && !isRideTrackingPage) {
        return <Navigate to={paths.tripHistoryDetail.replace(':id', reservation.id)} replace />;
    }

    const buildEditButtonInfos = (): JSX.Element => {
        console.log(reservation.status);
        if (!reservation || getInProgressStatus().includes(reservation.status)) {
            return isRideTrackingPage ? null : <Separator />;
        }

        if (isRideTrackingPage) {
            return null;
        }

        const currentNote = reservation.driver_note;
        const currentRideReason = reservation.ride_reason;

        const isPro = isRidePro(reservation);

        return (
            <>
                <EditButtonList
                    buttonsEntries={[
                        {
                            title: stringsRes.reservation.editReservation,
                            // subtitle: 'avec changement de prix',
                            onClick: onEditReservation,
                            id: 'editReservation',
                        },
                    ]}
                />
                <Separator />
                {currentNote ? (
                    <div className={classes.marginContainer}>
                        <NoteCard
                            title={stringsRes.booking.msgToDriverTitle}
                            note={currentNote}
                            onEdit={onMessageToDriver}
                        />
                    </div>
                ) : (
                    <>
                        <Space value={2} />
                        <TextButton
                            title={stringsRes.booking.msgToDriver}
                            id="msgToDriver"
                            onClick={onMessageToDriver}
                            startIcon={<AddOutlinedIcon />}
                        />
                        <Space value={2} />
                    </>
                )}
                {isPro && (
                    <>
                        <Separator height={1} />
                        {currentRideReason ? (
                            <div className={classes.marginContainer}>
                                <NoteCard
                                    title={stringsRes.booking.rideReasonTitle}
                                    note={currentRideReason}
                                    onEdit={onAddRideReason}
                                />
                            </div>
                        ) : (
                            <>
                                <Space value={2} />
                                <TextButton
                                    id="rideReason"
                                    title={stringsRes.booking.rideReason}
                                    onClick={onAddRideReason}
                                    startIcon={<AddOutlinedIcon />}
                                />
                                <Space value={2} />
                            </>
                        )}
                    </>
                )}
                <Separator />
            </>
        );
    };

    const buildButtonsList = (): EditButtonInfos[] => {
        const menuEntries: EditButtonInfos[] = [
            // { title: stringsRes.reservation.addToCalendar, onClick: onAddToCalendar, id: 'addToCalendar' },
        ];

        if (
            reservation &&
            !reservation.is_free_ride &&
            (!preloadReservation || isBookingFinishedNotCanceled(reservation.status))
        ) {
            menuEntries.push({
                title: stringsRes.reservation.bookReturnRide,
                onClick: onBookTheReturnRide,
                id: 'bookReturnRide',
            });
        }

        if (reservation?.status && getSOSStatus().includes(reservation.status) && (isWebView() || isMobileSize)) {
            menuEntries.push({
                title: stringsRes.reservation.sosReservation,
                onClick: onSOSReservation,
                id: 'sosReservation',
            });
        }

        if (recurrent) {
            menuEntries.push({
                title: stringsRes.reservation.pauseRecurrent,
                onClick: onPauseRecurrence,
                id: 'pauseRecurrence',
                disabled: reservation?.breaks?.some((b) => !b.deleted_at),
            });
        }

        if ((reservation?.status && getCancellableStatus().includes(reservation.status)) || recurrent) {
            menuEntries.push({
                title: recurrent ? stringsRes.reservation.cancelRecurrent : stringsRes.reservation.cancelReservation,
                onClick: handleCancelReservation,
                color: 'danger',
                id: 'cancelReservation',
            });
        }

        return menuEntries;
    };

    const onMessageToDriver = (): void => {
        openModal(ModalType.EDITION, {
            title: stringsRes.booking.msgToDriver,
            subtitle: stringsRes.booking.driverMessageDesc,
            children: <EditReservationDriverMessage />,
            modalSize: ModalSize.xl,
            labelConfirm: reservation?.driver_note ? stringsRes.common.edit : stringsRes.common.add,
            reservation,
        });
    };

    const onAddRideReason = (): void => {
        openModal(ModalType.EDITION, {
            title: stringsRes.booking.rideReasonTitle,
            children: <EditReservationRideReason />,
            modalSize: ModalSize.xl,
            labelConfirm: reservation?.ride_reason ? stringsRes.common.edit : stringsRes.common.add,
            reservation,
        });
    };

    const onSOSReservation = (): void => {
        // Old way - moving to a sos view
        // navigate(paths.sos, {
        //     state: { id, sosActivated: reservation?.emergency?.enabled },
        // });

        openModal(ModalType.CONFIRMATION, {
            title: stringsRes.sos.title,
            description: stringsRes.sos.descriptionLong,
            descriptionProps: { customColor: 'black' },
            containerClassName: classes.sosModal,
            labelCancel: stringsRes.sos.action,
            typeCancel: ButtonType.CANCEL,
            labelConfirm: stringsRes.sos.actionUrgence,
            typeConfirm: ButtonType.CONFIRM,
            modalSize: ModalSize.md,
            modalPosition: 'bottom',
            onCancelAction: () => {
                sosActivate.mutate(
                    { id: reservation.id },
                    {
                        onSuccess: (data: Ride) => {
                            reservation.emergency = data?.emergency ?? { enabled: true, history: [] };
                        },
                    },
                );
            },
            onConfirmAction: () => {
                const phoneNumber = `${isWebView() ? '//' : ''}112`;
                window.location.href = `tel:${phoneNumber}`;
            },
        });
    };

    const onCancelSos = (): void => {
        if (reservation?.id) {
            sosCancel.mutate(
                { id: reservation.id },
                {
                    onSuccess: (data: Ride) => {
                        reservation.emergency = data?.emergency ?? { enabled: false, history: [] };
                    },
                },
            );
        }
    };

    const onCancelBreak = (currentBreak: Break): void => {
        if (currentBreak?.id) {
            unPauseRecurrence.mutate(
                { break_id: currentBreak.id },
                {
                    onSuccess: () => {
                        client.invalidateQueries(`recurrings${reservation.id}`);
                    },
                },
            );
        }
    };

    const onPauseRecurrence = (): void => {
        openModal(ModalType.RECURRENCE, {
            title: stringsRes.recurrence.pause,
            description: stringsRes.recurrence.title,
            labelConfirm: stringsRes.recurrence.pause,
            modalSize: ModalSize.sm,
            onConfirmAction: (formData: { start_date: Moment; end_date: Moment }) => {
                // console.log({ formData });
                pauseRecurrence.mutate(
                    {
                        recurring_id: id,
                        start_date: Math.floor(formData.start_date.startOf('day').valueOf() / 1000),
                        end_date: Math.floor(formData.end_date?.endOf('day')?.valueOf() / 1000),
                    },
                    {
                        onSuccess: () => {
                            client.invalidateQueries(`recurrings${reservation.id}`);
                        },
                    },
                );
            },
        });
    };

    const prefillStep = (): void => {
        prefillRide(reservation, paymentList);
        setIsEditing(true);
        setShouldAvoidPoi(false);
        setCanSkipPoi(true);
    };

    const onEditReservation = (): void => {
        prefillStep();
        navigate(paths.home);
    };

    const onEditReservationServices = (): void => {
        prefillStep();
        navigate(paths.homeService);
    };

    const onEditPoiNumber = (): void => {
        prefillStep();
        const pickup = findStop(reservation.stops, StopType.PICKUP);
        navigate(isAirport(pickup) ? paths.homeScheduleFlight : paths.homeScheduleStation);
    };

    const onEditReservationFromDate = (): void => {
        prefillStep();
        const pickup = findStop(reservation.stops, StopType.PICKUP);
        navigate(isAirport(pickup) ? paths.homeScheduleFlight : paths.homeSchedule);
    };

    // TODO: feat - implement add to calendar
    // const onAddToCalendar = (): void => {};

    const onBookTheReturnRide = (): void => {
        prefillRide(reservation, paymentList);
        returnTrip();
        navigate(paths.homeSchedule);
    };

    // LT-1884 — l'etat du paiement, ecrit dans le detail de la course. La
    // modale (bookingTrackingMobile) ne s'ouvre qu'une fois par etat et le
    // badge de la liste ne dit que « a regulariser » : ici, en cliquant sur la
    // course, le passager lit ce qui se passe et ce qu'il risque, sans rien
    // rouvrir (retour operateur, 17/09). Avec deux rappels mail seulement dans
    // toute la sequence (LT-1930), cet encart porte une part de l'alerte.
    //
    // Rien quand la validation suit son cours : elle doit paraitre acquise.
    // Seuls le refus et l'annulation s'ecrivent.
    const renderClearanceNotice = (): ReactElement | null => {
        if (!reservation) return null;
        if (isRideCancelledForPayment(reservation)) {
            return (
                <InfoBox color="error">
                    <Text.Description14 text={stringsRes.paymentClearance.detailsCancelled} />
                </InfoBox>
            );
        }
        if (isClearanceBlocked(reservation)) {
            return (
                <InfoBox color="warning">
                    <div className={classes.clearanceNoticeBody}>
                        <Text.Description14 text={stringsRes.paymentClearance.detailsBlocked} />
                        <TextButton
                            id="fixPaymentMethod"
                            title={stringsRes.paymentClearance.detailsBlockedCta}
                            onClick={() => navigate(paths.payment)}
                        />
                    </div>
                </InfoBox>
            );
        }
        return null;
    };
    const clearanceNotice = renderClearanceNotice();

    return (
        <PageContainer title={isMobileSize ? stringsRes.reservation.reservation : undefined}>
            {!reservation || !paymentList ? (
                <LoaderFullPage color="secondary" />
            ) : (
                <>
                    {isRideTrackingPage && (
                        <div className={clsx(globalClasses.pageOpenCloseContainer, classes.pageOpenCloseContainer)}>
                            <div className={globalClasses.pageOpenClose} />
                        </div>
                    )}
                    <ReservationCard
                        reservation={reservation}
                        title={stringsRes.reservation.reservation}
                        openModal={openModal}
                        onEditPoiNumber={onEditPoiNumber}
                        onEditReservationService={onEditReservationServices}
                        onEditReservationFromDate={
                            !reservation || getInProgressStatus().includes(reservation.status)
                                ? null
                                : onEditReservationFromDate
                        }
                        onCancelSos={onCancelSos}
                        onCancelBreak={onCancelBreak}
                    />
                    {clearanceNotice && (
                        <>
                            {clearanceNotice}
                            <Space value={2} />
                        </>
                    )}
                    {/*
                        <TextButton
                            onClick={onBookTheReturnRide}
                            title={stringsRes.reservation.bookReturnRide} id="bookReturnRide"
                            startIcon={<AddOutlinedIcon/>}
                        /> 
                    */}
                    <>
                        <Separator height={1} />
                        {!recurrent && buildEditButtonInfos()}
                        <EditButtonList buttonsEntries={buildButtonsList()} />
                    </>
                </>
            )}
        </PageContainer>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // InfoBox lays its children out in a row; the blocked notice stacks a
        // sentence over its button.
        clearanceNoticeBody: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: theme.spacing(1),
        },
        marginContainer: {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(1),
        },
        pageOpenCloseContainer: {
            padding: '16px 0px 16px 0px',
        },
        sosModal: {
            '& > div:last-child': {
                flexDirection: 'column',
                gap: 12,
                '& > button': {
                    width: '100%',
                    maxWidth: 'unset',
                },
            },
        },
    }),
);

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    prefillRide: currentBookingActions.prefillRide,
    returnTrip: currentBookingActions.returnTrip,
    setIsEditing: currentBookingActions.setIsEditing,
    setShouldAvoidPoi: currentBookingActions.setShouldAvoidPoi,
    setCanSkipPoi: currentBookingActions.setCanSkipPoi,
    openModal: modalActions.openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReservationDetails);
